import { useState } from "react";
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Printer, Loader2, BookOpen, ChevronRight } from "lucide-react";

interface SyllabusNode { title: string; description: string | null; is_key_node: boolean }
interface SyllabusChapter {
  id: string; title: string; description: string | null;
  order_index: number; nodes: SyllabusNode[];
}
interface SyllabusObjective { title: string; description: string | null }
interface SyllabusMaterial { file_name: string; file_type: string; summary: string | null }
interface WebSource { title: string; url: string; summary: string | null }
interface SyllabusData {
  course: { title: string; description: string | null };
  objectives: SyllabusObjective[];
  chapters: SyllabusChapter[];
  materials: SyllabusMaterial[];
  webSources: WebSource[];
  generatedAt: string;
}

interface SyllabusPrintViewProps {
  courseId: string;
  courseTitle: string;
}

// ── HTML document builder ─────────────────────────────────────────────────────
function buildPrintHTML(data: SyllabusData): string {
  const fmt = (d: string) =>
    new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  const objectivesHtml = data.objectives.length
    ? `<section class="section">
        <h2 class="section-title">Learning Objectives</h2>
        <ol class="objectives-list">
          ${data.objectives
            .map(
              (o) => `<li class="objective-item">
              <span class="objective-title">${esc(o.title)}</span>
              ${o.description ? `<span class="objective-desc">${esc(o.description)}</span>` : ""}
            </li>`
            )
            .join("")}
        </ol>
      </section>`
    : "";

  const chaptersHtml = data.chapters.length
    ? `<section class="section">
        <h2 class="section-title">Course Structure</h2>
        ${data.chapters
          .map(
            (ch, ci) => `<div class="chapter">
            <div class="chapter-header">
              <span class="chapter-number">Chapter ${ci + 1}</span>
              <span class="chapter-title">${esc(ch.title)}</span>
            </div>
            ${ch.description ? `<p class="chapter-desc">${esc(ch.description)}</p>` : ""}
            ${
              ch.nodes.length
                ? `<ul class="node-list">
                ${ch.nodes
                  .map(
                    (n) => `<li class="node-item">
                    <span class="node-title${n.is_key_node ? " key-node" : ""}">
                      ${esc(n.title)}${n.is_key_node ? " <span class='key-badge'>Key Assessment</span>" : ""}
                    </span>
                    ${n.description ? `<span class="node-desc">${esc(n.description)}</span>` : ""}
                  </li>`
                  )
                  .join("")}
              </ul>`
                : ""
            }
          </div>`
          )
          .join("")}
      </section>`
    : "";

  const materialsHtml = data.materials.length
    ? `<section class="section">
        <h2 class="section-title">Sources Consulted</h2>
        <p class="section-intro">The following materials were used in the design and generation of this course.</p>
        <ul class="sources-list">
          ${data.materials
            .map(
              (m) => `<li class="source-item">
              <span class="source-name">${esc(m.file_name)}</span>
              <span class="source-type">${esc(m.file_type.toUpperCase())}</span>
              ${m.summary ? `<span class="source-summary">${esc(m.summary)}</span>` : ""}
            </li>`
            )
            .join("")}
        </ul>
      </section>`
    : "";

  const webSourcesHtml = (data.webSources ?? []).length
    ? `<section class="section">
        <h2 class="section-title">Research Sources</h2>
        <p class="section-intro">Web sources gathered during course research and outline generation.</p>
        <ul class="sources-list">
          ${(data.webSources ?? [])
            .map(
              (s) => `<li class="source-item">
              <span class="source-name">${esc(s.title)}</span>
              ${s.summary ? `<span class="source-summary">${esc(s.summary)}</span>` : ""}
              ${s.url ? `<a class="source-url" href="${esc(s.url)}" target="_blank">${esc(s.url)}</a>` : ""}
            </li>`
            )
            .join("")}
        </ul>
      </section>`
    : "";

  const hasBody =
    data.objectives.length > 0 ||
    data.chapters.length > 0 ||
    data.materials.length > 0 ||
    (data.webSources ?? []).length > 0;

  const emptyBodyHtml = !hasBody
    ? `<section class="section empty-state" aria-label="No syllabus content">
        <p class="empty-state-text">
          No objectives, course structure, or sources were included in this export. Regenerate the syllabus from the course page, or confirm the course has outline and objective data.
        </p>
      </section>`
    : "";

  return `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<meta name="viewport" content="width=device-width, initial-scale=1.0" />
<title>${esc(data.course.title)} — Course Syllabus</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');

  :root {
    --green: #1C3829;
    --green-mid: #2F6147;
    --cream: #EAE6DC;
    --cream-dark: #DDD8CB;
    --lime: #C8D46A;
    --text: #1C3829;
    --text-muted: #4A6B58;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  html, body {
    height: auto;
    min-height: 0;
  }

  body {
    font-family: 'DM Sans', Georgia, sans-serif;
    background: var(--cream);
    color: var(--text);
    line-height: 1.6;
    font-size: 13px;
  }

  .page {
    max-width: 820px;
    margin: 0 auto;
    padding: 0 32px 48px;
    background: var(--cream);
    min-height: 0;
  }

  /* ── Header ── */
  .header {
    background: var(--green);
    margin: 0 -32px;
    padding: 36px 32px 28px;
    position: relative;
  }
  .header-eyebrow {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--lime);
    margin-bottom: 10px;
  }
  .header-title {
    font-size: 28px;
    font-weight: 700;
    color: #ffffff;
    line-height: 1.2;
    margin-bottom: 10px;
    font-family: Georgia, serif;
  }
  .header-description {
    font-size: 13px;
    color: rgba(255,255,255,0.75);
    max-width: 600px;
    line-height: 1.6;
  }
  .header-meta {
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid rgba(255,255,255,0.12);
    font-size: 10px;
    color: rgba(255,255,255,0.5);
    letter-spacing: 0.04em;
  }

  /* ── Sections ── */
  .section {
    margin-top: 36px;
  }
  .section-title {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--text-muted);
    margin-bottom: 14px;
    padding-bottom: 8px;
    border-bottom: 1px solid rgba(44,97,71,0.2);
  }
  .section-intro {
    font-size: 12px;
    color: var(--text-muted);
    margin-bottom: 14px;
    font-style: italic;
  }

  .empty-state {
    margin-top: 24px;
    padding: 18px 20px;
    background: #fff;
    border-radius: 8px;
    border: 1px dashed rgba(44,97,71,0.28);
  }
  .empty-state-text {
    font-size: 13px;
    color: var(--text-muted);
    line-height: 1.55;
  }

  /* ── Objectives ── */
  .objectives-list {
    list-style: none;
    counter-reset: obj;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .objective-item {
    counter-increment: obj;
    display: flex;
    gap: 12px;
    align-items: flex-start;
    padding: 10px 14px;
    background: var(--cream-dark);
    border-radius: 6px;
  }
  .objective-item::before {
    content: counter(obj);
    font-size: 11px;
    font-weight: 700;
    color: var(--lime);
    background: var(--green);
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 1px;
  }
  .objective-title {
    font-weight: 600;
    font-size: 13px;
    display: block;
  }
  .objective-desc {
    font-size: 11.5px;
    color: var(--text-muted);
    display: block;
    margin-top: 3px;
  }

  /* ── Chapters ── */
  .chapter {
    margin-bottom: 22px;
    padding: 16px 18px;
    background: #fff;
    border-radius: 8px;
    border: 1px solid rgba(44,97,71,0.12);
  }
  .chapter-header {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 6px;
  }
  .chapter-number {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #fff;
    background: var(--green);
    padding: 2px 8px;
    border-radius: 3px;
    white-space: nowrap;
  }
  .chapter-title {
    font-size: 15px;
    font-weight: 700;
    color: var(--text);
    font-family: Georgia, serif;
  }
  .chapter-desc {
    font-size: 12px;
    color: var(--text-muted);
    margin-bottom: 12px;
    line-height: 1.6;
  }
  .node-list {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 7px;
    padding-left: 4px;
  }
  .node-item {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 7px 10px 7px 12px;
    border-left: 2px solid rgba(44,97,71,0.2);
  }
  .node-title {
    font-size: 12.5px;
    font-weight: 600;
    color: var(--text);
    display: flex;
    align-items: center;
    gap: 7px;
  }
  .node-title.key-node { color: var(--green-mid); }
  .key-badge {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    background: var(--lime);
    color: var(--green);
    padding: 1px 6px;
    border-radius: 3px;
  }
  .node-desc {
    font-size: 11px;
    color: var(--text-muted);
    line-height: 1.55;
  }

  /* ── Sources ── */
  .sources-list {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .source-item {
    padding: 11px 14px;
    background: var(--cream-dark);
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    gap: 3px;
  }
  .source-name {
    font-weight: 600;
    font-size: 12.5px;
  }
  .source-type {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.07em;
    color: var(--green-mid);
  }
  .source-meta {
    font-size: 11px;
    color: var(--text-muted);
    font-style: italic;
  }
  .source-summary {
    font-size: 11.5px;
    color: var(--text-muted);
    line-height: 1.55;
  }
  .source-url {
    font-size: 10.5px;
    color: var(--green-mid);
    text-decoration: none;
    word-break: break-all;
  }

  /* ── Footer ── */
  .footer {
    margin-top: 32px;
    padding-top: 14px;
    border-top: 1px solid rgba(44,97,71,0.2);
    font-size: 10px;
    color: var(--text-muted);
    display: flex;
    justify-content: space-between;
  }

  /* ── Print ── */
  @media print {
    /* Do not use break-inside: avoid on .section — one section wraps the whole course structure and
       forces the entire block to the next page, leaving the first page as header + blank space. */
    @page { margin: 0.5in; size: letter portrait; }
    html, body { -webkit-print-color-adjust: exact; print-color-adjust: exact; height: auto; min-height: 0; }
    body { background: var(--cream); }
    .page { padding: 0 0 28px; max-width: 100%; }
    .header {
      margin: 0 0 20px;
      padding: 28px 24px 22px;
      break-after: avoid;
      page-break-after: avoid;
    }
    .section-title { break-after: avoid; page-break-after: avoid; }
    .chapter { break-inside: avoid; page-break-inside: avoid; }
    .objective-item { break-inside: avoid; page-break-inside: avoid; }
    .source-item { break-inside: avoid; page-break-inside: avoid; }
    .empty-state { break-inside: avoid; page-break-inside: avoid; }
    .footer { break-inside: avoid; page-break-inside: avoid; margin-top: 24px; }
    .print-btn { display: none !important; }
  }

  /* ── Print button (screen only) ── */
  .print-btn {
    position: fixed;
    top: 20px;
    right: 24px;
    background: var(--green);
    color: var(--lime);
    border: none;
    padding: 10px 20px;
    border-radius: 6px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 7px;
    box-shadow: 0 4px 16px rgba(28,56,41,0.2);
    z-index: 100;
  }
  .print-btn:hover { background: #2F6147; }
</style>
</head>
<body>
<button class="print-btn" onclick="window.print()">
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
  Print / Save as PDF
</button>

<div class="page">
  <div class="header">
    <div class="header-eyebrow">Course Syllabus</div>
    <h1 class="header-title">${esc(data.course.title)}</h1>
    ${data.course.description ? `<p class="header-description">${esc(data.course.description)}</p>` : ""}
    <div class="header-meta">Generated ${fmt(data.generatedAt)} · Distilled Intuition</div>
  </div>

  ${objectivesHtml}
  ${chaptersHtml}
  ${materialsHtml}
  ${webSourcesHtml}
  ${emptyBodyHtml}

  <div class="footer">
    <span>${esc(data.course.title)} — Course Syllabus</span>
    <span>Distilled Intuition · distilledintuition.training</span>
  </div>
</div>
</body>
</html>`;
}

function esc(s: string | null | undefined): string {
  if (!s) return "";
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function SyllabusPrintView({ courseId, courseTitle }: SyllabusPrintViewProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<SyllabusData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchAndOpen = async () => {
    setOpen(true);
    if (data) return; // already loaded
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error("Not authenticated");
      const { data: res, error: fnErr } = await invokeFunction("generate-syllabus", {
        body: { courseId },
      });
      if (fnErr) throw fnErr;
      setData(res as SyllabusData);
    } catch (e: any) {
      setError(e?.message || "Failed to generate syllabus");
    } finally {
      setLoading(false);
    }
  };

  const openPrintWindow = () => {
    if (!data) return;
    const html = buildPrintHTML(data);
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(html);
    w.document.close();
  };

  return (
    <>
      <Button variant="outline" size="sm" onClick={fetchAndOpen} className="gap-2">
        <Printer className="h-4 w-4" />
        <span className="hidden sm:inline">Print Syllabus</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Course Syllabus
            </DialogTitle>
            <DialogDescription>
              AI-generated syllabus with learning objectives, curriculum, and recommended sources.
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="flex flex-col items-center gap-4 py-12 text-muted-foreground">
              <Loader2 className="h-8 w-8 animate-spin" />
              <p className="text-sm">Generating syllabus and researching additional sources…</p>
            </div>
          )}

          {error && (
            <div className="py-8 text-center text-sm text-destructive">{error}</div>
          )}

          {data && !loading && (
            <div className="space-y-6">
              {/* Preview summary */}
              <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                <div>
                  <h3 className="font-semibold text-base">{data.course.title}</h3>
                  {data.course.description && (
                    <p className="text-sm text-muted-foreground mt-1">{data.course.description}</p>
                  )}
                </div>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-muted-foreground">
                  <span>{data.objectives.length} learning objective{data.objectives.length !== 1 ? "s" : ""}</span>
                  <span>{data.chapters.length} chapter{data.chapters.length !== 1 ? "s" : ""}</span>
                  <span>{data.chapters.reduce((s, c) => s + c.nodes.length, 0)} skill nodes</span>
                  <span>{data.materials.length} uploaded material{data.materials.length !== 1 ? "s" : ""}</span>
                  <span>{(data.webSources ?? []).length} research source{(data.webSources ?? []).length !== 1 ? "s" : ""}</span>
                </div>
              </div>

              {/* Chapter preview */}
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Chapters</p>
                {data.chapters.map((ch, i) => (
                  <div key={ch.id} className="rounded-md border px-3 py-2.5 text-sm">
                    <div className="flex items-center gap-2 font-medium">
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span>Ch. {i + 1}: {ch.title}</span>
                      <span className="ml-auto text-xs text-muted-foreground">{ch.nodes.length} nodes</span>
                    </div>
                    {ch.description && (
                      <p className="text-xs text-muted-foreground mt-1 pl-5">{ch.description}</p>
                    )}
                  </div>
                ))}
              </div>

              <Button onClick={openPrintWindow} className="w-full gap-2">
                <Printer className="h-4 w-4" />
                Open Print View (Print or Save as PDF)
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
